import * as Matter from 'matter-js';
import type { BuiltScene } from '../scenes';
import { drawBody, drawConstraint, type BodyStyle } from './body';
import { drawVector, type VectorStyle } from './vector';
import { drawTrace, type TraceHistory } from './trace';
import { computeForcesOn } from '../physics/forces';

export interface RenderState {
  scene: BuiltScene;
  engine: Matter.Engine;
  bodyStyle: BodyStyle;
  vectorStyle: VectorStyle;
  showForces: boolean;
  showTrace: boolean;
  trace?: TraceHistory;
  traceColor?: string;
  selectedBody: Matter.Body | null;
  selectionColor: string;
  forceLabel: (labelKey: string) => string;
  // Mutated as forces are computed for the current frame. Caller resets on scene change.
  forcesSeen?: Set<string>;
  // labelKeys in this set are computed (so they still register in forcesSeen) but not drawn.
  hiddenForces?: Set<string>;
}

// Draw a thin outline that follows the body's actual vertices, plus a tiny corner tag.
// Less visually intrusive than a bounding circle, and the user can see exactly which body
// is being held by the mouse constraint.
function drawSelectionOutline(
  ctx: CanvasRenderingContext2D,
  body: Matter.Body,
  color: string,
): void {
  const verts = body.vertices;
  if (verts.length === 0) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(verts[0]!.x, verts[0]!.y);
  for (let i = 1; i < verts.length; i++) ctx.lineTo(verts[i]!.x, verts[i]!.y);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

export function drawWorld(ctx: CanvasRenderingContext2D, state: RenderState): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  if (state.showTrace && state.trace) {
    drawTrace(ctx, state.trace, state.traceColor ?? '#888');
  }

  for (const c of state.scene.constraints) drawConstraint(ctx, c, state.bodyStyle);
  for (const body of state.scene.bodies) drawBody(ctx, body, state.bodyStyle);

  if (state.selectedBody) {
    drawSelectionOutline(ctx, state.selectedBody, state.selectionColor);
  }

  state.scene.drawOverlay?.(ctx);

  if (state.showForces) {
    for (const body of state.scene.bodies) {
      if (body.isStatic) continue;
      const forces = computeForcesOn(body, state.engine, state.scene);
      for (const f of forces) {
        if (state.forcesSeen) state.forcesSeen.add(f.labelKey);
        if (state.hiddenForces?.has(f.labelKey)) continue;
        drawVector(ctx, f, state.forceLabel(f.labelKey), state.vectorStyle);
      }
    }
  }
}
